// Used in teacher.module.ts and courseLanguage.guard.ts
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatSelectChange } from "@angular/material/select";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { Router } from "@angular/router";
// rxjs
import { of, Subject } from "rxjs";
import { delay, takeUntil, tap } from "rxjs/operators";
// store
import { select, Store } from "@ngrx/store";
import {
    Course,
    Language,
    PPUser as User
} from "cli-types";

import { LaunchIntentService } from "#core/services/launch-intent.service";
import { LocalStorage } from "#core/services/local-storage.service";
import * as fromStore from "#core/teacher-classes/store";
import { LanguageHelper } from "#helpers/language.helper";
import { SnackBarService } from "#services/snackbar.service";
import { MatAccordion } from '@angular/material/expansion';
import { AssetAssociation, Edition, Level } from "@carnegie-learning/cure-types";
import { CoursesService, SchoolsFidoService, SchoolsService, StudentPaginationParameters, TeachersService } from "@carnegie-learning/data-access-angular-passport";

interface CourseEditing {
    classProfile: string;
    editionId: string;
    language: Language | null;
    levelLabel: string;
    levelSelected: any | null; // not completely certain what this should be
    subjectId: string;
    standardSet: string;
    archive: boolean;
}
interface SelectAllObj extends CourseEditing {
    checked?: boolean;
}

@Component({
    selector: "roster-teacher-class-language-form",
    templateUrl: "./roster-course-language-form.component.html",
    styleUrls: ["./roster-course-language-form.component.scss"],
    standalone: false
})
export class RosterCourseLanguageFormComponent implements OnInit, OnDestroy {
    @ViewChild(MatAccordion) accordion: MatAccordion;
    saveText = "Continue";
    activeCoursesData: Course[] = [];
    activeLanguages: Language[] = [];
    activeEditions: Edition[];
    activeSubjects: AssetAssociation[];
    archiveCoursesPayload: Course[] = [];
    languagesLoaded = false;
    currentObj: { [index: number]: CourseEditing } = {};
    selectAllObj: SelectAllObj = {
        checked: false,
        classProfile: "",
        editionId: "",
        language: null,
        levelLabel: "",
        levelSelected: null,
        subjectId: "",
        standardSet: "",
        archive: false
    };
    schoolName = "";
    levelSelectedAllCourses = true; // false, a level has been selected for each teacher-class, else some do not
    singleCourse = false;
    singleLanguage = false;
    zeroLanguage = false;
    levelLabel = "";
    // Local Storage Profile
    user: User | undefined;
    isTeacher = false;
    teacherID = 0;
    classProfiles = [
        { value: "Core", id: 0 },
        { value: "Core Advanced", id: 1 },
        { value: "Special Education", id: 2 },
        { value: "Intervention", id: 3 },
        { value: "Honors", id: 4 },
        { value: "Extension", id: 5 },
        { value: "Group 1", id: 6 },
        { value: "Group 2", id: 7 },
        { value: "Group 3", id: 8 },
        { value: "Group 4", id: 9 },
        { value: "Group 5", id: 10 },
    ];
    subjects: AssetAssociation[];
    showWarning = true;

    private ngUnsubscribe = new Subject();

    constructor(
        private router: Router,
        private store: Store<fromStore.CoursesState>,
        private localStorage: LocalStorage,
        private schoolsService: SchoolsService,
        private teachersService: TeachersService,
        private schoolsFidoService: SchoolsFidoService,
        private languageHelper: LanguageHelper,
        private coursesService: CoursesService,
        private snackBarService: SnackBarService,
        private launchIntentService: LaunchIntentService
    ) {}

    ngOnInit() {
        const teacherProfileData = this.localStorage.get("userProfile");
        if (
            teacherProfileData !== undefined ||
            teacherProfileData !== "" ||
            teacherProfileData !== null
        ) {
            this.user = teacherProfileData;
            const teacherSchoolID: string = teacherProfileData.schools[0];
            this.teacherID = teacherProfileData.userID;
            this.teachersService
                .getTeacherCoursesNoProgram(this.teacherID)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((classes) => {
                    if (classes && classes.length > 0) {
                        this.activeCoursesData = classes;
                        this.singleCourse = !!(
                            this.activeCoursesData.length === 1
                        );
                        // Set up the display teacher-class object
                        const params: StudentPaginationParameters = {
                            filter: [],
                            filter_column_range: undefined,
                            limit: 100,
                            page: 0,
                            search: "",
                            sorting: { lastName: "asc" },
                            withCourseMetrics: true,
                        };

                        // Get Student data to add to course object.
                        this.activeCoursesData.forEach((course, index) => {
                            this.coursesService
                                .getStudents(params, course.courseID)
                                .pipe(takeUntil(this.ngUnsubscribe))
                                .subscribe((students) => {
                                    course = {
                                        ...course,
                                        students: students.data,
                                    };
                                    this.activeCoursesData[index] = course;
                                });
                            this.currentObj[course.courseID] = {
                                classProfile: "",
                                editionId: "",
                                language: null,
                                levelLabel: this.levelLabel,
                                levelSelected: null,
                                subjectId: "",
                                standardSet: "",
                                archive: false
                            };
                        });
                    }
                });

            this.schoolsService
                .getActivePrograms(teacherSchoolID)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((languages: Language[]) => {
                    if (languages) {
                        this.activeLanguages = languages;
                        // Check if there any available languages and set flags
                        this.zeroLanguage = !languages.length;
                        this.singleLanguage = languages.length === 1;
                        this.languagesLoaded = true;
                    }
                });

            this.schoolsFidoService
                .getSchool(teacherSchoolID)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((school) => {
                    this.schoolName = school.name;
                });

            this.store
                .pipe(select(fromStore.getAllActiveEditions))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((editions: Edition[]) => {
                    this.activeEditions = editions;
                });

            this.store
                .pipe(select(fromStore.getAllActiveSubjects))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((subjects: AssetAssociation[]) => {
                    this.activeSubjects = subjects;
                });

            // The level selector is built from activeEditions/activeSubjects
            // (see getLevels). In the normal flow the teacher side-nav dispatches
            // these loads, but in LTI intent mode the side-nav is not rendered
            // (teacher.component hides it when isTeacherIntentMode), so nothing
            // fills these store slices and no levels appear. Dispatch the loads
            // here ONLY in intent mode, so non-intent launches are completely
            // untouched (the side-nav remains the sole loader there, with no risk
            // of a duplicate in-flight fetch).
            if (this.launchIntentService.isIntentMode()) {
                if (!this.activeEditions.length) {
                    this.store.dispatch(
                        fromStore.LoadActiveEditions({
                            payload: teacherSchoolID,
                        })
                    );
                }
                if (!this.activeSubjects.length) {
                    this.store.dispatch(
                        fromStore.LoadActiveSubjects({
                            payload: teacherSchoolID,
                        })
                    );
                }
            }
        }
    }

    checkLevels() {
        let counter = 0,
            tempCourse;
        for (const x in this.currentObj) {
            tempCourse = this.currentObj[x];
            if (tempCourse.levelLabel !== null) {
                counter++;
            }
        }
        this.levelSelectedAllCourses = !(
            counter === this.activeCoursesData.length
        );
    }

    getLevels(course: CourseEditing) {
        const edition = this.activeEditions.find(
            (e) => e.editionId === course.editionId
        );
        const subject = this.activeSubjects.find(
            (e) => e.subjectId === course.subjectId
        );
        // this.levelLabel = "Level";
        if (subject && subject.levels && edition && edition.levelIds) {
            let levels: Level[] = [];
            const editionLevelIds = edition.levelIds;
            const subjectLevels = subject.levels;

            editionLevelIds.forEach((l) => {
                const foundSubjectLevel = subjectLevels.find(
                    (sl) => sl.levelId === l
                );
                if (foundSubjectLevel) {
                    levels.push(foundSubjectLevel);
                }
            });
            levels = levels.sort(
                (a, b) =>
                    subjectLevels.findIndex((i) => a.levelId === i.levelId) -
                    subjectLevels.findIndex((i) => b.levelId === i.levelId)
            );
            return levels;
        }
    }

    setLanguageForAllCourses(event: MatSlideToggleChange, crse: Course) {
        this.selectAllObj.checked = event.checked;
        if (event.checked) {
            this.selectAllObj = {
                ...this.selectAllObj,
                classProfile: this.currentObj[crse.courseID].classProfile,
                editionId: this.currentObj[crse.courseID].editionId,
                language: this.currentObj[crse.courseID].language,
                levelLabel: this.currentObj[crse.courseID].levelLabel,
                levelSelected: this.currentObj[crse.courseID].levelSelected,
                standardSet: this.currentObj[crse.courseID].standardSet,
                subjectId: this.currentObj[crse.courseID].subjectId,
            };
            // Add all spine levels for all teacher-classes by default
            this.activeCoursesData.forEach((course) => {
                this.currentObj[course.courseID] = {
                    ...this.currentObj[course.courseID],
                    classProfile: this.selectAllObj.classProfile,
                    editionId: this.selectAllObj.editionId,
                    language: this.selectAllObj.language,
                    levelLabel: this.selectAllObj.levelLabel
                        ? this.selectAllObj.levelLabel
                        : "",
                    levelSelected: this.selectAllObj.levelSelected,
                    standardSet: this.selectAllObj.standardSet,
                    subjectId: this.selectAllObj.subjectId,
                };
            });
            this.levelLabel = this.selectAllObj.language
                ? this.selectAllObj.language?.levelLabel
                : "";
        } else {
            this.selectAllObj = {
                ...this.selectAllObj,
                classProfile: "",
                language: null,
                levelLabel: "",
                levelSelected: null,
                standardSet: "",
                subjectId: "",
            };
        }
        this.checkLevels();
    }

    setLanguageForOneCourse(data: MatSelectChange, courseID: number) {
        // ngModel sets language
        this.currentObj[courseID] = {
            ...this.currentObj[courseID],
            editionId: data.value.editionId,
            levelLabel: "",
            levelSelected: null,
            subjectId: data.value.subjectId,
        }; // Clear out any previous language settings
        this.checkLevels();
    }

    createPayload(): Course[] {
        const payload: Course[] = [];
        this.activeCoursesData.forEach((course) => {
            if (this.currentObj[course.courseID].language && !this.currentObj[course.courseID].archive) {
                const levels = this.getLevels(this.currentObj[course.courseID]);
                let classProfile =
                    this.currentObj[course.courseID].classProfile;
                // check if the classProfile is an index of classProfiles[] and POST the value
                if (Number.isInteger(classProfile)) {
                    classProfile = this.classProfiles[<any>classProfile].value;
                }
                const coursePayload: Course = {
                    ...course,
                    classProfile,
                    editionId: this.currentObj[course.courseID].editionId,
                    language: <Language>(
                        this.currentObj[course.courseID].language
                    ),
                    languageID: <number>(
                        this.currentObj[course.courseID].language?.languageID
                    ),
                    level:
                        this.currentObj[course.courseID].levelLabel,
                    standardSet: this.currentObj[course.courseID].standardSet,
                    subjectId: this.currentObj[course.courseID].subjectId,
                    students: course.students,
                };
                if (coursePayload.language) {
                    payload.push(coursePayload);
                }
            } else if (this.currentObj[course.courseID].archive) { // PAS-13920 create separate payload to archive classes
                this.archiveCoursesPayload.push(course);
            }
        });
        return payload;
    }

    submitDisabled() {
        let allHavePrograms = true;
        let allHaveLevels = true;
        this.activeCoursesData.forEach((course) => {
            // PAS-13920 add archive flag as bypass for validation
            if (!this.currentObj[course.courseID].archive) {
                const program = this.currentObj[course.courseID].editionId;
                if (!program) {
                    allHavePrograms = false;
                }
                const level = this.currentObj[course.courseID].levelLabel;
                if (!level) {
                    allHaveLevels = false;
                }
            }
        });
        if (!allHavePrograms || !allHaveLevels) {
            this.showWarning = true;
            return true;
        }
    }

    // Sleep function using RxJS
    sleep(duration: number) {
        return of(null).pipe(
            delay(duration),
            tap(() => console.log(`Slept for ${duration / 1000} seconds`))
        ).toPromise();
    }

    // Method that uses the sleep function
    async runTask() {
        this.snackBarService.working("Processing Request...");
        await this.sleep(5000); // Sleep for 5 seconds
        this.snackBarService.success("Archiving Completed, redirecting back to homepage.");
        (<any>window).location.href = (<any>window).passportHost +
            `/#/teacher/classes`;
    }

    ok() {
        const payload: Course[] = this.createPayload();
        // PAS-13920 send out async payload request to archive classes
        if (this.archiveCoursesPayload && this.archiveCoursesPayload.length > 0) {
            this.archiveCoursesPayload.forEach((course) => {
                this.coursesService.archiveCourse(course).subscribe();
            });
        }
        if (payload && payload.length > 0) {
            this.store.dispatch(
                fromStore.updateRosterCoursesBulk({ payload: payload })
            );
            // this.router.navigate(["/teacher/classes"]);
        } else {
            // PAS-13920 all classes were selected to archive so redirect user to homepage
            this.runTask();
        }
    }

    completenessFlag(course: Course): string {
        return (this.currentObj[course.courseID].language &&
            this.currentObj[course.courseID].levelSelected) ||
            this.currentObj[course.courseID].archive
            ? "Complete"
            : "Incomplete";
    }

    trackByLanguageID(index: number, item: Language) {
        return item.languageID;
    }

    isMath(subjectId: string) {
        return subjectId && this.languageHelper.ifMathLanguage(subjectId);
    }

    applyAllDisabled(course: Course): boolean {
        let verdict = true;
        if (
            course &&
            this.currentObj[course.courseID].subjectId
        ) {
            verdict = !this.currentObj[course.courseID].language;
        } else {
            verdict =
                !this.currentObj[course.courseID].language ||
                !this.currentObj[course.courseID].levelLabel
            ;
        }
        return verdict;
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
